"use client";

import OptionContent, { OptionImage } from "./OptionContent";

import { getFoodTypeLabel } from "@/lib/food-types";

import { configurationError, foodFlavorCounts } from "@/lib/food-configuration";

import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Heart,
  MessageCircle,
  Package,
  ShoppingCart,
  Star,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import FoodCompositionSelector from "@/components/store/FoodCompositionSelector";
import { quotePublicItems, type ItemQuote } from "@/lib/store";
import type { CompositionSelection } from "@/types/food-configuration";

import LeadCaptureModal from "@/components/store/LeadCaptureModal";

import { formatCurrency, normalizeWhatsapp } from "@/lib/order";
import { REAL_ESTATE_PROPERTY_TYPES } from "@/lib/real-estate";
import type { BusinessConfig } from "@/lib/business-config";
import type { CartItemConfiguration } from "@/types/cart";
import type { PublicProduct, PublicProductVariation } from "@/types/store";

type VariationSelection = Record<number, string>;

type ProductDetailsModalProps = {
  catalogOnly?: boolean;
  hideWhatsapp?: boolean;
  quoteItems?: typeof quotePublicItems;
  product: PublicProduct;
  businessConfig: BusinessConfig;
  companySlug: string;
  companyName: string;
  whatsapp: string;
  foodAvailabilityLabel?: string;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onClose: () => void;
  onAdd: (
    variation: PublicProductVariation | null,
    configuration: CartItemConfiguration,
  ) => void;
  onWhatsapp: () => void;
};

function buildProductWhatsappUrl(
  whatsapp: string,
  companyName: string,
  product: PublicProduct,
  businessConfig: BusinessConfig,
) {
  const number = normalizeWhatsapp(whatsapp);

  const reference =
    businessConfig.businessType === "real_estate" && product.reference_code
      ? ` — Ref. ${product.reference_code}`
      : "";

  const message =
    `Olá! Encontrei a empresa ${companyName} ` +
    `no fornecedor.net e tenho interesse no ` +
    `${businessConfig.itemSingularLower} ` +
    `"${product.name}"${reference}.`;

  return `https://wa.me/${number}?text=` + encodeURIComponent(message);
}

function getAttributeValues(product: PublicProduct, attributeId: number) {
  return Array.from(
    new Set(
      product.variations.flatMap((variation) =>
        variation.values
          .filter((item) => item.attribute === attributeId)
          .map((item) => item.value),
      ),
    ),
  );
}

function variationMatchesSelection(
  variation: PublicProductVariation,
  selection: VariationSelection,
) {
  return Object.entries(selection).every(([attributeId, selectedValue]) =>
    variation.values.some(
      (item) =>
        item.attribute === Number(attributeId) && item.value === selectedValue,
    ),
  );
}

function isOptionPossible(
  product: PublicProduct,
  selection: VariationSelection,
  attributeId: number,
  value: string,
) {
  const candidateSelection = {
    ...selection,
    [attributeId]: value,
  };

  return product.variations.some(
    (variation) =>
      variation.is_active &&
      variation.is_available &&
      variationMatchesSelection(variation, candidateSelection),
  );
}

function PropertyDetail({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl bg-white px-4 py-3 shadow-sm">
      <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
        {label}
      </p>

      <p className="mt-1 text-sm font-black text-slate-900">{value}</p>
    </div>
  );
}

type DetailItemProps = {
  label: string;
  value: string | number;
};

function DetailItem({ label, value }: DetailItemProps) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>

      <p className="mt-1 text-sm font-bold text-slate-900">{value}</p>
    </div>
  );
}

export default function ProductDetailsModal({
  catalogOnly = false,
  hideWhatsapp = false,
  product,
  businessConfig,
  companySlug,
  companyName,
  whatsapp,
  foodAvailabilityLabel,
  isFavorite,
  onToggleFavorite,
  onClose,
  onAdd,
  onWhatsapp,
  quoteItems = quotePublicItems,
}: ProductDetailsModalProps) {
  const promotionOffer =
    product.promotion_offer ?? null;

  const [selection, setSelection] = useState<VariationSelection>({});

  const [leadCaptureOpen, setLeadCaptureOpen] = useState(false);

  const [optionSelection, setOptionSelection] = useState<
    Record<number, number[]>
  >({});

  const [composition, setComposition] = useState<Record<number, CompositionSelection[]>>({});
  const [compositionCounts, setCompositionCounts] = useState<Record<number, number>>({});
  const [quoteState, setQuoteState] = useState<{key: string; item?: ItemQuote; error?: string} | null>(null);

  const [observation, setObservation] = useState("");

  const [quantity, setQuantity] = useState(1);

  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [descriptionOverflows, setDescriptionOverflows] = useState(false);

  useEffect(() => {
    if (!promotionOffer) {
      return;
    }

    if (promotionOffer.variation_id) {
      const variation =
        product.variations.find(
          (item) =>
            item.id ===
            promotionOffer.variation_id,
        );

      if (variation) {
        const lockedSelection:
          VariationSelection = {};

        variation.values.forEach(
          (value) => {
            lockedSelection[
              value.attribute
            ] = value.value;
          },
        );

        setSelection(
          lockedSelection
        );
      }
    }

    if (
      promotionOffer.option_id &&
      promotionOffer.option_group_id
    ) {
      setComposition((current) => ({
        ...current,
        [promotionOffer.option_group_id!]: [
          {
            option_id:
              promotionOffer.option_id!,
            fraction: "1.0",
          } as CompositionSelection,
        ],
      }));

      setCompositionCounts(
        (current) => ({
          ...current,
          [promotionOffer.option_group_id!]:
            1,
        }),
      );
    }
  }, [
    product.id,
    product.variations,
    promotionOffer?.promotion_item_id,
    promotionOffer?.variation_id,
    promotionOffer?.option_id,
    promotionOffer?.option_group_id,
  ]);

  const selectedVariation = useMemo(() => {
    if (!product.has_variations) {
      return null;
    }

    const selectedEntries = Object.entries(selection);

    return (
      product.variations.find(
        (variation) =>
          variation.is_active &&
          variation.is_available &&
          variation.values.length === selectedEntries.length &&
          variationMatchesSelection(variation, selection),
      ) ?? null
    );
  }, [product.has_variations, product.variations, selection]);

  const allAttributesSelected =
    !product.has_variations || Boolean(selectedVariation);

  const activeOptionGroups = (product.option_groups ?? []).map(group => {
    const rule = group.variation_rules?.find(rule => rule.variation === selectedVariation?.id);
    return {...group,
      min_selections: rule?.min_selections ?? group.min_selections,
      max_selections: rule?.max_selections ?? group.max_selections,
      options: group.options.filter(option => {
        const rule = option.variation_rules?.find(rule => rule.variation === selectedVariation?.id);
        return group.configuration_type === "composition" ? !!rule && rule.is_active && rule.is_available && rule.full_price != null : !rule || (rule.is_active && rule.is_available);
      }).map(option => ({...option, price_delta: option.variation_rules?.find(rule => rule.variation === selectedVariation?.id)?.price_delta ?? option.price_delta})),
    };
  });
  const compositionComplete = activeOptionGroups.filter(group => group.configuration_type === "composition").every(group => {
    const selected = composition[group.id] ?? [];
    if (!group.required && !compositionCounts[group.id] && selected.length === 0) return true;
    const counts = foodFlavorCounts(group, selectedVariation?.id ?? null);
    const count = compositionCounts[group.id] ?? counts[0];
    return counts.includes(count) && selected.length === count &&
      new Set(selected.map(entry => entry.option_id)).size === count &&
      selected.every(entry => group.options.some(option => option.id === entry.option_id) && entry.fraction === (count === 1 ? "1.0" : "0.5"));
  });
  const compositionSelections = Object.values(composition).flat();
  const quoteItem = {
    product: product.id, variation: selectedVariation?.id ?? null, quantity,
    selected_option_ids: Object.values(optionSelection).flat(),
    composition_selections: compositionSelections, observation: observation.trim(),
  };
  const quoteKey = JSON.stringify(quoteItem);
  const isFood = businessConfig.businessType === "food";
  const showWhatsapp = !hideWhatsapp && (businessConfig.commerceMode === "lead" || !isFood);
  useEffect(() => {
    if (catalogOnly || !isFood || !compositionComplete || (product.has_variations && !selectedVariation)) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      quoteItems(companySlug, [JSON.parse(quoteKey)]).then(response => {
        if (!cancelled) setQuoteState({key: quoteKey, item: response.items[0]});
      }).catch(error => {
        if (!cancelled) setQuoteState({key: quoteKey, error: configurationError(error)});
      });
    }, 200);
    return () => {cancelled = true; clearTimeout(timer);};
  }, [quoteKey, isFood, catalogOnly, companySlug, product.has_variations, selectedVariation, compositionComplete, quoteItems]);
  const currentQuote = quoteState?.key === quoteKey ? quoteState : null;
  const configuredUnitPrice = Number(currentQuote?.item?.unit_price ?? selectedVariation?.current_price ?? product.current_price);
  const configuredTotal = configuredUnitPrice * quantity;
  const canAdd = !catalogOnly && product.is_available && product.food_category_available !== false && allAttributesSelected && (!isFood || (compositionComplete && !!currentQuote?.item));
  const displayedPrice = isFood && currentQuote?.item ? currentQuote.item.unit_price : selectedVariation?.current_price ?? product.current_price;

  const imageUrls = useMemo(
    () =>
      Array.from(
        new Set(
          [
            selectedVariation?.image_url,
            product.primary_image_url,
            ...(product.images ?? []).map((image) => image.image_url),
          ].filter((url): url is string => Boolean(url)),
        ),
      ),
    [product, selectedVariation?.image_url],
  );

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedVariation?.id, selectedVariation?.image_url]);

  const description =
    product.description ||
    product.short_description ||
    `Consulte a empresa para mais informações sobre este ${businessConfig.itemSingularLower}.`;

  const descriptionRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    setDescriptionExpanded(false);
  }, [product.id]);

  useEffect(() => {
    const element = descriptionRef.current;

    if (!element || descriptionExpanded) {
      return;
    }

    const checkOverflow = () => {
      setDescriptionOverflows(
        element.scrollHeight > element.clientHeight + 1,
      );
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [description, descriptionExpanded]);

  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);

  const currentImageUrl = imageUrls[currentImageIndex] ?? imageUrls[0] ?? null;

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);

      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  function showPreviousImage() {
    if (imageUrls.length <= 1) {
      return;
    }

    setCurrentImageIndex((current) =>
      current === 0 ? imageUrls.length - 1 : current - 1,
    );
  }

  function showNextImage() {
    if (imageUrls.length <= 1) {
      return;
    }

    setCurrentImageIndex((current) =>
      current === imageUrls.length - 1 ? 0 : current + 1,
    );
  }

  function handleTouchStart(event: React.TouchEvent<HTMLDivElement>) {
    if (imageUrls.length <= 1) {
      return;
    }

    const touch = event.touches[0];

    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
  }

  function handleTouchEnd(event: React.TouchEvent<HTMLDivElement>) {
    if (
      imageUrls.length <= 1 ||
      touchStartXRef.current === null ||
      touchStartYRef.current === null
    ) {
      return;
    }

    const touch = event.changedTouches[0];

    const deltaX = touch.clientX - touchStartXRef.current;

    const deltaY = touch.clientY - touchStartYRef.current;

    touchStartXRef.current = null;
    touchStartYRef.current = null;

    const minimumSwipeDistance = 50;

    if (
      Math.abs(deltaX) < minimumSwipeDistance ||
      Math.abs(deltaX) <= Math.abs(deltaY)
    ) {
      return;
    }

    if (deltaX < 0) {
      showNextImage();
      return;
    }

    showPreviousImage();
  }

  function selectValue(attributeId: number, value: string) {
    if (promotionOffer?.variation_id) {
      return;
    }

    setOptionSelection({});
    setComposition({});
    setCompositionCounts({});
    setSelection((current) => {
      if (current[attributeId] === value) {
        const next = { ...current };
        delete next[attributeId];
        return next;
      }

      return {
        ...current,
        [attributeId]: value,
      };
    });
  }

  function toggleProductOption(
    groupId: number,
    optionId: number,
    selectionType: "single" | "multiple",
    maxSelections: number,
  ) {
    setOptionSelection((current) => {
      const selected = current[groupId] ?? [];

      if (selectionType === "single") {
        return {
          ...current,
          [groupId]: selected.includes(optionId) && !activeOptionGroups.find(group => group.id === groupId)?.required ? [] : [optionId],
        };
      }

      if (selected.includes(optionId)) {
        return {
          ...current,
          [groupId]: selected.filter((id) => id !== optionId),
        };
      }

      if (selected.length >= maxSelections) {
        return current;
      }

      return {
        ...current,
        [groupId]: [...selected, optionId],
      };
    });
  }

  function handleAdd() {
    if (!canAdd) {
      return;
    }

    const selectedOptions = activeOptionGroups.flatMap((group) => {
      const selectedIds = optionSelection[group.id] ?? [];

      return group.options
        .filter((option) => selectedIds.includes(option.id))
        .map((option) => ({
          group_id: group.id,
          group_name: group.name,
          option_id: option.id,
          option_name: option.name,
          price_delta: option.price_delta,
        }));
    });

    onAdd(selectedVariation, {
      selected_options: currentQuote?.item?.option_selections ?? selectedOptions,
      composition_selections: compositionSelections,
      quoted_unit_price: currentQuote?.item?.unit_price,
      configuration_snapshot: currentQuote?.item?.configuration_snapshot,
      observation: observation.trim(),
      quantity,
    });
  }

  const whatsappUrl =
    product.is_available && whatsapp
      ? buildProductWhatsappUrl(whatsapp, companyName, product, businessConfig)
      : "";

  const addButtonLabel =
    product.has_variations && !selectedVariation
      ? "Selecione as opções"
      : !canAdd
        ? "Complete as escolhas"
        : `Adicionar • ${formatCurrency(String(configuredTotal))}`;

  return (
    <div className="fixed inset-0 z-[80] flex w-screen max-w-full items-end justify-center overflow-hidden bg-slate-950/65 backdrop-blur-sm sm:items-center sm:p-5">
      <button
        type="button"
        aria-label="Fechar detalhes"
        onClick={onClose}
        className="absolute inset-0 h-full w-full"
      />

      <section className="relative z-10 flex max-h-[96vh] min-w-0 w-full max-w-full flex-col overflow-hidden rounded-t-[2rem] bg-white shadow-2xl sm:max-h-[90vh] sm:max-w-6xl sm:rounded-[2rem]">
        <header className="flex min-w-0 items-center justify-between border-b border-slate-200 bg-white px-5 py-4 sm:px-6">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-600">
              Detalhes do {businessConfig.itemSingularLower}
            </p>

            <h2 className="mt-1 line-clamp-1 text-lg font-black text-slate-950 sm:text-xl">
              {product.name}
            </h2>

            {businessConfig.businessType === "real_estate" &&
              product.reference_code && (
                <p className="mt-1 text-xs font-bold text-slate-400">
                  Ref. {product.reference_code}
                </p>
              )}
          </div>

          <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100"
          >
            <X size={21} />
          </button>
        </header>

        <div className="product-modal-scroll min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden pb-24 sm:pb-0">
          <div className="grid min-w-0 max-w-full lg:grid-cols-2">
            <div className="min-w-0 max-w-full overflow-hidden border-b border-slate-200 bg-slate-100 lg:border-b-0 lg:border-r">
              <div
                className="relative aspect-square w-full max-w-full touch-pan-y overflow-hidden"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                {currentImageUrl ? (
                  <div
                    className={`absolute inset-0 bg-center bg-no-repeat ${
                      businessConfig.businessType === "vehicles" ||
                      businessConfig.businessType === "real_estate"
                        ? "bg-contain"
                        : "bg-cover"
                    }`}
                    style={{
                      backgroundImage: `url("${currentImageUrl}")`,
                    }}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-slate-300">
                    <Package size={64} />
                  </div>
                )}

                {product.availability_status !== "available" ? (
                  <span className="absolute left-4 top-4 z-10 inline-flex rounded-full bg-rose-600 px-3 py-1.5 text-xs font-black uppercase tracking-wide text-white shadow">
                    {product.availability_status === "sold"
                      ? "Vendido"
                      : businessConfig.businessType === "retail" ||
                          businessConfig.businessType === "wholesale"
                        ? "Esgotado"
                        : "Indisponível"}
                  </span>
                ) : product.is_featured ? (
                  <span
                    title="Produto em destaque"
                    aria-label="Produto em destaque"
                    className="m-2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-amber-200 bg-white/95 text-amber-500 shadow-sm backdrop-blur-sm sm:m-3 sm:h-8 sm:w-8"
                  >
                    <Star
                      size={13}
                      strokeWidth={2.5}
                      className="fill-current sm:h-[17px] sm:w-[17px]"
                    />
                  </span>
                ) : null}

                <button
                  type="button"
                  aria-label={
                    isFavorite
                      ? `Remover ${product.name} dos favoritos`
                      : `Adicionar ${product.name} aos favoritos`
                  }
                  title={
                    isFavorite
                      ? "Remover dos favoritos"
                      : "Adicionar aos favoritos"
                  }
                  aria-pressed={isFavorite}
                  onClick={onToggleFavorite}
                  className={`absolute right-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border bg-white/95 shadow-sm backdrop-blur-sm transition hover:scale-110 ${
                    isFavorite
                      ? "border-rose-200 text-rose-500"
                      : "border-slate-200 text-slate-500 hover:text-rose-500"
                  }`}
                >
                  <Heart
                    size={19}
                    className={
                      isFavorite ? "fill-rose-500 text-rose-500" : "fill-none"
                    }
                  />
                </button>

                {imageUrls.length > 1 && (
                  <>
                    <button
                      type="button"
                      aria-label="Imagem anterior"
                      onClick={showPreviousImage}
                      className="absolute left-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)] transition hover:scale-110 sm:flex"
                    >
                      <ChevronLeft size={22} />
                    </button>

                    <button
                      type="button"
                      aria-label="Próxima imagem"
                      onClick={showNextImage}
                      className="absolute right-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)] transition hover:scale-110 sm:flex"
                    >
                      <ChevronRight size={22} />
                    </button>

                  </>
                )}
              </div>

              {imageUrls.length > 1 && (
                <div className="flex w-full max-w-full gap-3 overflow-x-auto overscroll-x-contain border-t border-slate-200 bg-white p-4">
                  {imageUrls.map((imageUrl, index) => (
                    <button
                      type="button"
                      key={imageUrl}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 bg-slate-100 ${
                        index === currentImageIndex
                          ? "border-blue-600"
                          : "border-transparent"
                      }`}
                    >
                      <div
                        className="h-full w-full bg-cover bg-center"
                        style={{
                          backgroundImage: `url("${imageUrl}")`,
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}

              <div className="border-t border-slate-200 bg-white p-5 sm:p-6">
                <h4 className="text-base font-black text-slate-950">
                  Descrição
                </h4>

                <div className="relative mt-3">
                  <p
                    ref={descriptionRef}
                    className={`whitespace-pre-line break-words text-sm leading-7 text-slate-600 ${
                      !descriptionExpanded
                        ? "max-h-28 overflow-hidden"
                        : ""
                    }`}
                  >
                    {description}
                  </p>

                  {descriptionOverflows && !descriptionExpanded && (
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />
                  )}
                </div>

                {(descriptionOverflows || descriptionExpanded) && (
                  <button
                    type="button"
                    onClick={() =>
                      setDescriptionExpanded((current) => !current)
                    }
                    className="mt-3 text-sm font-black text-blue-600 transition hover:text-blue-700"
                    aria-expanded={descriptionExpanded}
                  >
                    {descriptionExpanded
                      ? "Mostrar menos"
                      : "Ver descrição completa"}
                  </button>
                )}
              </div>
            </div>

            <div className="min-w-0 max-w-full p-5 sm:p-7">
              <p className="break-words text-xs font-bold uppercase tracking-wide text-blue-600">
                {product.category_name || businessConfig.itemSingular}
              </p>

              <h3 className="mt-2 max-w-full break-words text-2xl font-black tracking-tight text-slate-950 sm:text-3xl">
                {product.name}
              </h3>

              <div className="mt-5">
                {!selectedVariation && product.promotional_price && (
                  <p className="text-sm font-medium text-slate-400 line-through">
                    {formatCurrency(product.price)}
                  </p>
                )}

                {selectedVariation?.promotional_price &&
                  selectedVariation.price && (
                    <p className="text-sm font-medium text-slate-400 line-through">
                      {formatCurrency(selectedVariation.price)}
                    </p>
                  )}

                <p className="max-w-full break-words text-3xl font-black text-slate-950">
                  {isFood &&
                  Number(product.current_price) === 0 &&
                  Number(displayedPrice) === 0
                    ? "Escolha as opções"
                    : formatCurrency(displayedPrice)}
                </p>
              </div>

              {businessConfig.businessType === "food" && (
                <div className="mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <h4 className="text-sm font-black text-slate-900">
                    Informações do item
                  </h4>

                  <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {product.business_attributes.food_type && (
                      <PropertyDetail
                        label="Tipo"
                        value={
                          getFoodTypeLabel(product.business_attributes.food_type)
                        }
                      />
                    )}

                    {product.business_attributes.preparation_time_minutes !==
                      undefined && (
                      <PropertyDetail
                        label="Preparo"
                        value={`${product.business_attributes.preparation_time_minutes} min`}
                      />
                    )}

                    {product.business_attributes.serves_people !==
                      undefined && (
                      <PropertyDetail
                        label="Serve"
                        value={
                          product.business_attributes.serves_people === 1
                            ? "1 pessoa"
                            : `${product.business_attributes.serves_people} pessoas`
                        }
                      />
                    )}
                  </div>

                  {product.business_attributes.complementary_note && (
                    <div className="mt-4 border-t border-slate-200 pt-4">
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                        Observação
                      </p>

                      <p className="mt-1 whitespace-pre-line text-sm leading-6 text-slate-700">
                        {product.business_attributes.complementary_note}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {businessConfig.businessType === "vehicles" && (
                <div className="mt-5">
                  <h3 className="text-sm font-bold text-slate-900">
                    Informações do veículo
                  </h3>

                  <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {product.business_attributes.brand && (
                      <DetailItem
                        label="Marca"
                        value={product.business_attributes.brand}
                      />
                    )}

                    {product.business_attributes.model && (
                      <DetailItem
                        label="Modelo"
                        value={product.business_attributes.model}
                      />
                    )}

                    {product.business_attributes.version && (
                      <DetailItem
                        label="Versão"
                        value={product.business_attributes.version}
                      />
                    )}

                    {(product.business_attributes.manufacture_year !==
                      undefined ||
                      product.business_attributes.model_year !== undefined) && (
                      <DetailItem
                        label="Ano"
                        value={[
                          product.business_attributes.manufacture_year,
                          product.business_attributes.model_year,
                        ]
                          .filter(
                            (value) => value !== undefined && value !== null,
                          )
                          .join("/")}
                      />
                    )}

                    {product.business_attributes.mileage !== undefined && (
                      <DetailItem
                        label="Quilometragem"
                        value={
                          product.business_attributes.mileage.toLocaleString(
                            "pt-BR",
                          ) + " km"
                        }
                      />
                    )}

                    {product.business_attributes.fuel_type && (
                      <DetailItem
                        label="Combustível"
                        value={
                          {
                            gasoline: "Gasolina",
                            ethanol: "Etanol",
                            flex: "Flex",
                            diesel: "Diesel",
                            hybrid: "Híbrido",
                            electric: "Elétrico",
                            other: "Outro",
                          }[product.business_attributes.fuel_type] ??
                          product.business_attributes.fuel_type
                        }
                      />
                    )}

                    {product.business_attributes.transmission && (
                      <DetailItem
                        label="Câmbio"
                        value={
                          {
                            manual: "Manual",
                            automatic: "Automático",
                            cvt: "CVT",
                            automated: "Automatizado",
                            other: "Outro",
                          }[product.business_attributes.transmission] ??
                          product.business_attributes.transmission
                        }
                      />
                    )}

                    {product.business_attributes.color && (
                      <DetailItem
                        label="Cor"
                        value={product.business_attributes.color}
                      />
                    )}

                    {product.business_attributes.condition && (
                      <DetailItem
                        label="Condição"
                        value={
                          {
                            new: "Novo",
                            semi_new: "Seminovo",
                            used: "Usado",
                          }[product.business_attributes.condition] ??
                          product.business_attributes.condition
                        }
                      />
                    )}
                  </div>

                  {product.business_attributes.optional_features &&
                    product.business_attributes.optional_features.length >
                      0 && (
                      <div className="mt-5">
                        <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">
                          Opcionais
                        </h4>

                        <div className="mt-2 flex flex-wrap gap-2">
                          {product.business_attributes.optional_features.map(
                            (feature) => (
                              <span
                                key={feature}
                                className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700"
                              >
                                {feature}
                              </span>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                </div>
              )}

              {businessConfig.businessType === "real_estate" && (
                <div className="mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <h4 className="text-sm font-black text-slate-900">
                    Características do imóvel
                  </h4>

                  <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {product.reference_code && (
                      <PropertyDetail
                        label="Referência"
                        value={product.reference_code}
                      />
                    )}

                    {product.business_attributes.area_m2 !== undefined && (
                      <PropertyDetail
                        label="Área"
                        value={`${product.business_attributes.area_m2} m²`}
                      />
                    )}

                    {product.business_attributes.bedrooms !== undefined && (
                      <PropertyDetail
                        label="Dormitórios"
                        value={String(product.business_attributes.bedrooms)}
                      />
                    )}

                    {product.business_attributes.suites !== undefined && (
                      <PropertyDetail
                        label="Suítes"
                        value={String(product.business_attributes.suites)}
                      />
                    )}

                    {product.business_attributes.bathrooms !== undefined && (
                      <PropertyDetail
                        label="Banheiros"
                        value={String(product.business_attributes.bathrooms)}
                      />
                    )}

                    {product.business_attributes.parking_spaces !==
                      undefined && (
                      <PropertyDetail
                        label="Vagas"
                        value={String(
                          product.business_attributes.parking_spaces,
                        )}
                      />
                    )}

                    {product.business_attributes.transaction_type && (
                      <PropertyDetail
                        label="Finalidade"
                        value={
                          product.business_attributes.transaction_type ===
                          "sale"
                            ? "Venda"
                            : "Locação"
                        }
                      />
                    )}

                    {product.business_attributes.property_type && (
                      <PropertyDetail
                        label="Tipo"
                        value={
                          REAL_ESTATE_PROPERTY_TYPES[
                            product.business_attributes.property_type
                          ]
                        }
                      />
                    )}

                    {product.business_attributes.condominium_fee !==
                      undefined && (
                      <PropertyDetail
                        label="Condomínio"
                        value={formatCurrency(
                          String(product.business_attributes.condominium_fee),
                        )}
                      />
                    )}

                    {product.business_attributes.property_tax !== undefined && (
                      <PropertyDetail
                        label="IPTU"
                        value={formatCurrency(
                          String(product.business_attributes.property_tax),
                        )}
                      />
                    )}
                  </div>

                  {(product.business_attributes.neighborhood ||
                    product.business_attributes.city) && (
                    <div className="mt-4 border-t border-slate-200 pt-4">
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                        Localização
                      </p>

                      <p className="mt-1 break-words text-sm font-bold text-slate-700">
                        {[
                          product.business_attributes.neighborhood,
                          product.business_attributes.city,
                        ]
                          .filter(Boolean)
                          .join(" — ")}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {!catalogOnly && businessConfig.commerceMode === "order" && (
                <div className="mt-4 flex items-center gap-2 text-sm font-bold">
                  {product.food_category_available === false ? (
                    <span className="font-bold text-amber-700">Fora do horário de venda</span>
                  ) : product.is_available ? (
                    <>
                      <CheckCircle2 size={18} className="text-emerald-500" />

                      <span className="text-emerald-700">Disponível</span>
                    </>
                  ) : (
                    <span className="font-bold text-rose-600">
                      {product.availability_status === "sold"
                        ? "Vendido"
                        : businessConfig.businessType === "retail" ||
                            businessConfig.businessType === "wholesale"
                          ? "Esgotado"
                          : "Indisponível"}
                    </span>
                  )}
                </div>
              )}

              {businessConfig.supportsStock &&
                !product.has_variations &&
                product.size && (
                  <div className="mt-5">
                    <span className="inline-flex rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">
                      Tamanho:{" "}
                      <strong className="ml-1 text-slate-950">
                        {product.size}
                      </strong>
                    </span>
                  </div>
                )}

              {businessConfig.supportsVariations && product.has_variations && (
                <div className="mt-6 space-y-5 border-t border-slate-200 pt-6">
                  {product.attributes.map((attribute) => {
                    const values = getAttributeValues(product, attribute.id);

                    return (
                      <fieldset key={attribute.id}>
                        <legend className="text-sm font-black text-slate-900">
                          {attribute.name}
                        </legend>

                        <div className="mt-3 flex flex-wrap gap-2">
                          {values.map((value) => {
                            const isSelected =
                              selection[attribute.id] === value;

                            const isPossible = isOptionPossible(
                              product,
                              selection,
                              attribute.id,
                              value,
                            );

                            return (
                              <button
                                type="button"
                                key={value}
                                disabled={Boolean(promotionOffer?.variation_id) || (!isSelected && !isPossible)}
                                onClick={() => selectValue(attribute.id, value)}
                                className={`min-h-11 rounded-xl border px-4 text-sm font-bold transition ${
                                  isSelected
                                    ? "border-blue-600 bg-blue-600 text-white"
                                    : isPossible
                                      ? "border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50"
                                      : "cursor-not-allowed border-slate-100 bg-slate-100 text-slate-300 line-through"
                                }`}
                              >
                                {value}
                              </button>
                            );
                          })}
                        </div>
                      </fieldset>
                    );
                  })}

                  {!allAttributesSelected && (
                    <p className="rounded-2xl bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700">
                      Selecione uma combinação disponível para continuar.
                    </p>
                  )}

                  {allAttributesSelected && !selectedVariation && (
                    <p className="rounded-2xl bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
                      Esta combinação não está disponível.
                    </p>
                  )}

                  {selectedVariation && (
                    <div className="flex items-center gap-2 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">
                      <CheckCircle2 size={17} className="text-emerald-500" />
                      {selectedVariation.display_name}
                    </div>
                  )}
                </div>
              )}

              {businessConfig.businessType === "food" && (
                  <div className="mt-7 space-y-6 border-t border-slate-200 pt-6">
                    {promotionOffer?.locked_configuration && (
                    <div className="mb-4 rounded-2xl border border-blue-200 bg-blue-50 p-4">
                      <p className="text-xs font-black uppercase tracking-wide text-blue-600">
                        Configuração desta oferta
                      </p>

                      <p className="mt-1 text-sm font-black text-slate-900">
                        {[
                          promotionOffer.variation_name,
                          promotionOffer.option_name,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>

                      {promotionOffer.option_id && (
                        <p className="mt-1 text-xs font-semibold text-slate-500">
                          1 sabor · pizza inteira
                        </p>
                      )}

                      <p className="mt-2 text-xs text-slate-500">
                        Tamanho e sabor já estão definidos pela promoção.
                        Borda, adicionais e observação continuam disponíveis.
                      </p>
                    </div>
                  )}

                  {activeOptionGroups.map((group) => {
                      const selected = optionSelection[group.id] ?? [];

                      return (
                        <fieldset key={group.id} className="min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <legend className="text-base font-black text-slate-950">
                                {group.name}
                              </legend>

                              <p className="mt-1 text-xs font-semibold text-slate-500">
                                {group.required ? "Obrigatório" : "Opcional"}
                                {" • "}
                                {group.configuration_type === "composition"
                                  ? "Escolha os sabores"
                                  : group.selection_type === "single"
                                  ? "Escolha 1"
                                  : `Escolha até ${group.max_selections}`}
                              </p>
                            </div>

                            {group.required && (
                              <span className="shrink-0 rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-blue-700">
                                Obrigatório
                              </span>
                            )}
                          </div>

                          {group.configuration_type === "composition" ? (
                            <FoodCompositionSelector key={`${group.id}:${selectedVariation?.id ?? "simple"}`} group={group} variationId={selectedVariation?.id ?? null} value={composition[group.id] ?? []} locked={promotionOffer?.option_group_id === group.id} onChange={(value, count) => {
                              if (
                                promotionOffer?.option_group_id ===
                                group.id
                              ) {
                                return;
                              }

                              setComposition(current => ({...current, [group.id]: value}));
                              setCompositionCounts(current => ({...current, [group.id]: count}));
                            }} />
                          ) : <div className="mt-3 divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white">
                            {group.options.map((option) => {
                              const isSelected = selected.includes(option.id);

                              const isAtLimit =
                                group.selection_type === "multiple" &&
                                !isSelected &&
                                selected.length >= group.max_selections;

                              const priceDelta = Number(option.price_delta);

                              return (
                                <div key={option.id} className="relative">
                                  {option.image_url && <div className="absolute left-[52px] top-1/2 z-10 -translate-y-1/2"><OptionImage option={option} /></div>}
                                <button
                                  type="button"
                                  aria-pressed={isSelected}
                                  disabled={isAtLimit}
                                  onClick={() =>
                                    toggleProductOption(
                                      group.id,
                                      option.id,
                                      group.selection_type,
                                      group.max_selections,
                                    )
                                  }
                                  className="flex min-h-16 w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45"
                                >
                                  <span
                                    className={`flex h-6 w-6 shrink-0 items-center justify-center border-2 transition ${
                                      group.selection_type === "single"
                                        ? "rounded-full"
                                        : "rounded-lg"
                                    } ${
                                      isSelected
                                        ? "border-blue-600 bg-blue-600 text-white"
                                        : "border-slate-300 bg-white"
                                    }`}
                                  >
                                    {isSelected && <CheckCircle2 size={15} />}
                                  </span>

                                  {option.image_url && <span aria-hidden="true" className="h-12 w-12 shrink-0" />}
                                  <OptionContent option={option} />

                                  {priceDelta > 0 && (
                                    <span className="shrink-0 text-sm font-black text-slate-700">
                                      + {formatCurrency(option.price_delta)}
                                    </span>
                                  )}
                                </button>
                                </div>
                              );
                            })}
                          </div>

                          }
                          {group.configuration_type !== "composition" && group.selection_type === "multiple" &&
                            selected.length >= group.max_selections && (
                              <p className="mt-2 text-xs font-semibold text-slate-500">
                                Limite de {group.max_selections}{" "}
                                {group.max_selections === 1
                                  ? "escolha"
                                  : "escolhas"}{" "}
                                atingido.
                              </p>
                            )}
                        </fieldset>
                      );
                    })}

                    <div>
                      <label
                        htmlFor="product-observation"
                        className="text-base font-black text-slate-950"
                      >
                        Alguma observação?
                      </label>

                      <textarea
                        id="product-observation"
                        value={observation}
                        onChange={(event) => setObservation(event.target.value)}
                        maxLength={500}
                        rows={3}
                        placeholder="Ex.: sem cebola, molho separado..."
                        className="mt-3 w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                      />

                      <p className="mt-1 text-right text-[11px] font-semibold text-slate-400">
                        {observation.length}/500
                      </p>
                    </div>

                    <div className="flex items-center justify-between gap-4 rounded-2xl bg-slate-50 p-3">
                      <span className="text-sm font-black text-slate-800">
                        Quantidade
                      </span>

                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          aria-label="Diminuir quantidade"
                          disabled={quantity <= 1}
                          onClick={() =>
                            setQuantity((current) => Math.max(1, current - 1))
                          }
                          className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-xl font-black text-slate-700 disabled:opacity-40"
                        >
                          −
                        </button>

                        <span className="min-w-7 text-center text-base font-black text-slate-950">
                          {quantity}
                        </span>

                        <button
                          type="button"
                          aria-label="Aumentar quantidade"
                          onClick={() => setQuantity((current) => current + 1)}
                          className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-xl font-black text-white hover:bg-blue-700"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                )}

              {catalogOnly ? <p role="status" className="mt-4 text-sm text-slate-600">Cardápio disponível para consulta. Pedidos online não estão habilitados.</p> : isFood && currentQuote?.error && foodAvailabilityLabel ? (
                <div
                  role="status"
                  className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700"
                >
                  <p className="text-sm font-bold">
                    Loja fechada no momento.
                  </p>

                  {foodAvailabilityLabel.startsWith("Fechado · abre ") ? (
                    <p className="mt-1 text-sm font-semibold">
                      Voltaremos a receber pedidos{" "}
                      {foodAvailabilityLabel
                        .replace("Fechado · abre ", "")
                        .replace(/^hoje às /, "hoje às ")
                        .replace(/^amanhã às /, "amanhã, às ")
                        .replace(/^(.*) às /, "$1, às ")}.
                    </p>
                  ) : null}
                </div>
              ) : isFood ? (
                <p
                  role="status"
                  className={`mt-4 text-sm ${
                    currentQuote?.error
                      ? "text-rose-700"
                      : "text-slate-500"
                  }`}
                >
                  {!allAttributesSelected
                    ? "Escolha o tamanho para configurar seu item."
                    : !compositionComplete
                      ? "Complete a quantidade de sabores escolhida."
                      : currentQuote?.error ??
                        (!currentQuote?.item
                          ? "Atualizando preço…"
                          : `Total: ${formatCurrency(
                              String(configuredTotal),
                            )}`)}
                </p>
              ) : null}
              <div className="mt-8 hidden gap-3 sm:flex">
                {!catalogOnly && businessConfig.commerceMode === "order" && (
                  <button
                    type="button"
                    disabled={!canAdd}
                    onClick={handleAdd}
                    className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 text-sm font-black text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    <ShoppingCart size={19} />
                    {addButtonLabel}
                  </button>
                )}

                {showWhatsapp &&
                  (whatsappUrl ? (
                    businessConfig.commerceMode === "lead" ? (
                      <button
                        type="button"
                        onClick={() => setLeadCaptureOpen(true)}
                        className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-5 text-sm font-black text-white hover:bg-emerald-600"
                      >
                        <MessageCircle size={19} />
                        {businessConfig.primaryCta}
                      </button>
                    ) : (
                      <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={onWhatsapp}
                        className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-5 text-sm font-black text-white hover:bg-emerald-600"
                      >
                        <MessageCircle size={19} />
                        WhatsApp
                      </a>
                    )
                  ) : (
                    <button
                      type="button"
                      disabled
                      className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-slate-300 px-5 text-sm font-black text-white"
                    >
                      <MessageCircle size={19} />
                      {businessConfig.commerceMode === "lead"
                        ? businessConfig.primaryCta
                        : "WhatsApp"}
                    </button>
                  ))}
              </div>

            </div>
          </div>
        </div>

        <footer className="absolute bottom-0 left-0 right-0 z-20 w-full max-w-full overflow-hidden border-t border-slate-200 bg-white p-4 shadow-[0_-12px_30px_rgba(15,23,42,0.08)] sm:hidden">
          <div
            className={
              businessConfig.commerceMode === "lead" || !showWhatsapp
                ? "grid grid-cols-1 gap-3"
                : "grid grid-cols-2 gap-3"
            }
          >
            {!catalogOnly && businessConfig.commerceMode === "order" && (
              <button
                type="button"
                disabled={!canAdd}
                onClick={handleAdd}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 text-sm font-black text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {!isFood && <ShoppingCart size={19} />}
                {isFood ? addButtonLabel : product.has_variations
                  ? selectedVariation
                    ? "Adicionar"
                    : "Selecione"
                  : "Adicionar"}
              </button>
            )}

            {showWhatsapp &&
              (whatsappUrl ? (
                businessConfig.commerceMode === "lead" ? (
                  <button
                    type="button"
                    onClick={() => setLeadCaptureOpen(true)}
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-4 text-sm font-black text-white hover:bg-emerald-600"
                  >
                    <MessageCircle size={19} />
                    {businessConfig.primaryCta}
                  </button>
                ) : (
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={onWhatsapp}
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-4 text-sm font-black text-white hover:bg-emerald-600"
                  >
                    <MessageCircle size={19} />
                    WhatsApp
                  </a>
                )
              ) : (
                <button
                  type="button"
                  disabled
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-slate-300 px-4 text-sm font-black text-white"
                >
                  <MessageCircle size={19} />
                  {businessConfig.commerceMode === "lead"
                    ? businessConfig.primaryCta
                    : "WhatsApp"}
                </button>
              ))}
          </div>
        </footer>
      </section>

      {leadCaptureOpen && (
        <LeadCaptureModal
          companySlug={companySlug}
          companyName={companyName}
          whatsapp={whatsapp}
          product={product}
          businessConfig={businessConfig}
          onClose={() => setLeadCaptureOpen(false)}
          onWhatsapp={onWhatsapp}
        />
      )}
    </div>
  );
}
